// Single-series column chart (dataviz spec): ≤24px columns, 4px rounded data-end, hairline grid,
// clean ticks, per-bar hover/focus tooltip, and a table view so no value is hover-gated.
// The plot is drawn in real pixels of its container (redrawn on resize) — never scaled — so text
// and columns keep their proportions at any width.
import { html, raw, esc } from '../lib/dom';

const niceMax = (v?: any) => {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const m = [1, 2, 2.5, 5, 10].find((s?: any) => s * p >= v);
  return m * p;
};

// fmt is a function, so chart data stays in memory keyed by plot id instead of the DOM.
const plots = new Map();
let seq = 0;

function drawSvg({ points, fmt, height: H }: any, W?: any) {
  const padL = 48, padR = 4, padB = 24, padT = 10;
  const max = niceMax(Math.max(...points.map((p?: any) => p.value)));
  const band = (W - padL - padR) / points.length;
  const bw = Math.min(24, band * 0.6);
  const y = (v?: any) => padT + (H - padT - padB) * (1 - v / max);
  const ticks = [0, max / 2, max];
  const r = 4;
  const bar = (p?: any, i?: any) => {
    const x = padL + band * i + (band - bw) / 2;
    const top = y(p.value), base = H - padB;
    const h = base - top;
    const rr = Math.min(r, h / 2);
    const d = h <= 0.5 ? '' : `M${x} ${base}V${top + rr}Q${x} ${top} ${x + rr} ${top}H${x + bw - rr}Q${x + bw} ${top} ${x + bw} ${top + rr}V${base}Z`;
    return `<g class="cbar" tabindex="0" role="img" aria-label="${esc(p.label)}: ${esc(fmt(p.value))}" data-label="${esc(p.label)}" data-value="${esc(fmt(p.value))}">
      <rect x="${padL + band * i}" y="${padT}" width="${band}" height="${H - padT - padB}" fill="transparent"/>
      ${d ? `<path d="${d}"/>` : `<rect x="${x}" y="${base - 1}" width="${bw}" height="1" opacity=".4"/>`}
    </g>`;
  };
  // Thin x labels by the space they actually need (Mono 10px ≈ 6.2px per char + gap).
  const labelW = Math.max(...points.map((p?: any) => String(p.label).length)) * 6.2 + 14;
  const every = Math.max(1, Math.ceil(labelW / band));
  const shown = points.map((_?: any, i?: any) => i % every === 0);
  const last = points.length - 1;
  const prev = last - (last % every);
  if (!shown[last] && (last - prev) * band >= labelW) shown[last] = true;
  return `<svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" style="display:block;overflow:visible">
    ${ticks.map((t?: any) => `<line x1="${padL}" x2="${W - padR}" y1="${y(t)}" y2="${y(t)}" class="cgrid"/><text x="${padL - 8}" y="${y(t) + 3.5}" text-anchor="end" class="ctick">${esc(fmt(t))}</text>`).join('')}
    ${points.map(bar).join('')}
    ${points.map((p?: any, i?: any) => (shown[i] ? `<text x="${padL + band * i + band / 2}" y="${H - 6}" text-anchor="middle" class="ctick">${esc(p.label)}</text>` : '')).join('')}
  </svg>`;
}

// points: [{ label, value }]; fmt: value → string
export function columnChart(points?: any, { title, fmt = String, height = 180, id = 'chart' }: any = {}) {
  const key = `${id}-${++seq}`;
  const data: any = { points, fmt, height };
  plots.set(key, data);
  return html`<figure class="chart" data-chart="${id}" style="margin:0">
    ${title ? html`<figcaption class="row" style="margin-bottom:6px"><h3>${title}</h3><span class="spacer"></span><button class="btn sm ghost" data-chart-table>Table</button></figcaption>` : ''}
    <div class="chart-plot" data-plot="${key}" style="position:relative;height:${height}px">
      <div class="chart-svg">${raw(drawSvg(data, 640))}</div>
      <div class="ctip" hidden><strong></strong><span></span></div>
    </div>
    <div class="chart-table" hidden><table class="table"><thead><tr><th>Day</th><th class="num">Value</th></tr></thead><tbody>${points.map((p?: any) => html`<tr><td>${p.label}</td><td class="num">${fmt(p.value)}</td></tr>`)}</tbody></table></div>
  </figure>`;
}

// Size plots to their container, wire hover/focus tooltips + table toggle for every chart inside root.
// Returns a cleanup that stops resize tracking.
export function bindCharts(root?: any) {
  const observers: ResizeObserver[] = [];
  const keys: string[] = [];
  root.querySelectorAll('[data-chart]').forEach((fig?: any) => {
    const tip = fig.querySelector('.ctip');
    const plot = fig.querySelector('.chart-plot');
    const holder = plot.querySelector('.chart-svg');
    const data = plots.get(plot.dataset.plot);
    keys.push(plot.dataset.plot);

    let drawnW = 0;
    const redraw = () => {
      const w = Math.floor(plot.clientWidth);
      if (!data || w <= 0 || w === drawnW) return;
      drawnW = w;
      holder.innerHTML = drawSvg(data, w);
    };
    redraw();
    const ro = new ResizeObserver(redraw);
    ro.observe(plot);
    observers.push(ro);

    const show = (g?: any, evt?: any) => {
      tip.querySelector('strong').textContent = g.dataset.value;
      tip.querySelector('span').textContent = g.dataset.label;
      tip.hidden = false;
      const pr = plot.getBoundingClientRect();
      const gr = g.getBoundingClientRect();
      const x = evt && evt.clientX ? evt.clientX - pr.left : gr.left - pr.left + gr.width / 2;
      tip.style.left = Math.min(Math.max(x, 50), pr.width - 50) + 'px';
      tip.style.top = Math.max(gr.top - pr.top + 4, 0) + 'px';
      fig.querySelectorAll('.cbar.on').forEach((b?: any) => b.classList.remove('on'));
      g.classList.add('on');
    };
    const hide = () => { tip.hidden = true; fig.querySelectorAll('.cbar.on').forEach((b?: any) => b.classList.remove('on')); };
    // Delegated, so listeners survive redraws.
    holder.addEventListener('pointermove', (e?: any) => { const g = e.target.closest('.cbar'); g ? show(g, e) : hide(); });
    holder.addEventListener('pointerleave', hide);
    holder.addEventListener('focusin', (e?: any) => { const g = e.target.closest('.cbar'); if (g) show(g); });
    holder.addEventListener('focusout', hide);

    fig.querySelector('[data-chart-table]')?.addEventListener('click', (e?: any) => {
      const t = fig.querySelector('.chart-table');
      t.hidden = !t.hidden;
      plot.hidden = !t.hidden;
      e.currentTarget.textContent = t.hidden ? 'Table' : 'Chart';
      if (t.hidden) redraw();
    });
  });
  return () => { observers.forEach((o?: any) => o.disconnect()); keys.forEach((k?: any) => plots.delete(k)); };
}
